/**
 * Error Boundary component with Sentry integration
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them to Sentry (if initialized), and displays a fallback UI.
 *
 * Architecture: Always uses CustomErrorBoundary internally.
 * If Sentry is initialized, errors are captured via captureException in componentDidCatch.
 * This avoids conditional component selection on each render.
 */

import React from 'react';
import { Warning as AlertTriangle } from '@phosphor-icons/react';
import { captureException, isSentryInitialized } from '../config/sentry';
import './ErrorBoundary.css';

/**
 * Fallback UI component shown when an error occurs
 * Uses CSS theme variables for consistent styling across embed modes
 */
function ErrorFallback({ error, resetError }) {
  return (
    <div className="error-boundary">
      <div className="error-boundary__section">
        <AlertTriangle className="error-boundary__icon" aria-hidden="true" />
      </div>

      <h2 className="error-boundary__title">Something went wrong</h2>

      <p className="error-boundary__text">
        We apologize for the inconvenience. The error has been reported and we&apos;re working on fixing it.
      </p>

      {import.meta.env.DEV && error && (
        <details className="error-boundary__details">
          <summary className="error-boundary__summary">
            Error details (development only)
          </summary>
          <pre className="error-boundary__stack">
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}

      <div className="error-boundary__actions">
        {resetError && (
          <button onClick={resetError} className="error-boundary__btn">
            Try again
          </button>
        )}

        <button
          onClick={() => window.location.reload()}
          className="error-boundary__btn error-boundary__btn--secondary"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

/**
 * Error Boundary class component
 *
 * Always uses class-based error boundary (required by React for error catching).
 * If Sentry is initialized, errors are automatically reported via captureException.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Report to Sentry if initialized (checked inside captureException)
    if (isSentryInitialized()) {
      captureException(error, {
        componentStack: errorInfo.componentStack,
        source: 'ErrorBoundary',
      });
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;

      if (fallback) {
        return fallback({
          error: this.state.error,
          resetError: this.resetError,
        });
      }

      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * App Error Boundary wrapper
 * Simple wrapper for consistent API
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @param {Function} [props.fallback] - Custom fallback render function: ({ error, resetError }) => ReactNode
 */
export function AppErrorBoundary({ children, fallback }) {
  return (
    <ErrorBoundary fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
}

export default AppErrorBoundary;
